import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Phone, MapPin, ShieldCheck, ChevronUp, ArrowLeft, MessageCircle, RefreshCw, Clock } from 'lucide-react';
import { trackCallClick, trackMessengerClick, trackRouteClick, trackReservationOpen, TERMEK_CTA } from '../utils/analytics';
import useProductView from '../utils/useProductView';
import useCtaView from '../utils/useCtaView';
import { BUSINESS, PRIMARY_PHONE, getStoreContact, resolveStoreKey, OFFICE_STORE_FIELD_MAP } from '../constants/stores';
import { useActiveStoreEffect } from '../context/ActiveStoreContext';
import ReservationModal from '../components/ReservationModal';
import { resolveImageUrl } from '../utils/deviceImage';
import { batteryBenefit, storageBenefit, gradeBenefit } from '../utils/benefits';
import { konkretKeszulekElemek, arMikrosorElemek, AR_MIKROSOR_2 } from '../utils/termekBizonyitek';
import { getCategory } from '../constants/categories';
import { officeBattery } from '../constants/grades';
import { fetchJsonCached } from '../utils/apiCache';
import ConditionBadge, { InfoTip } from '../components/ConditionBadge';
import { normalizeGrade } from '../constants/grades';
import { getBrand } from '../constants/categories';
import { GARANCIA_HASZNALT_ROVID } from '../constants/garancia';
import { useBolt } from '../utils/boltMod';
import { ELLENORZES_DB, ELLENORZES_FOLYO } from '../constants/ellenorzes';
import { remaining } from '../utils/reservationTime';
import PaymentMethods from '../components/PaymentMethods';
import ShareButton from '../components/ShareButton';

// Készlet a saját backend proxyn át (P6.2) – token nélkül. A kép-URL feloldás
// a deviceImage util-ban (robusztus: relatív/„/”-hiányos/http→https esetek).
const STOREFRONT_API = `${process.env.REACT_APP_BACKEND_URL}/api/storefront/devices`;

// Az állapot-megjelenítés forrás-igazsága: constants/grades.js (ConditionBadge).

const normalizeName = (name = '') => name
  .replace(/\biphone\b/gi, 'iPhone')
  .replace(/\bairpods?\b/gi, 'AirPods')
  .replace(/(\d+)\s*gb\b/gi, '$1 GB');

const formatPrice = (price) =>
  price ? `${Number(price).toLocaleString('hu-HU')} Ft` : 'Ár egyeztetve';

const STORE_LABELS = {
  belvaros: 'Belvárosi üzlet – Rákóczi út 16',
  tesco:    'Tesco üzletsor – Vásárhelyi út 23/A',
};

export default function PhoneDetailPage() {
  // Bolt mód (/bolt/telefonok/:uid): szerviz-mentes hirdetési változat – utils/boltMod.js
  const { bolt, t, h } = useBolt();
  const { uid } = useParams();
  const [searchParams] = useSearchParams();
  const [device, setDevice]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // A betölthetetlen kép URL-je. Ha a fotó nem tölt be, a „Nincs saját fotó”
  // blokk jelenik meg – nem törött kép, és a „saját fotó” állítás is kimarad.
  const [hibasKep, setHibasKep] = useState(null);
  // Sprint 2A: 24 órás foglalás. A foglaltságot és a lejáratot a BACKEND dönti
  // el – a kliens csak megjeleníti, így nincs két igazságforrás.
  const [showReservation, setShowReservation] = useState(false);
  const [reserved, setReserved] = useState(null);   // null = még nem tudjuk
  // A sticky mobil sáv „Hívás” gombja ettől hívja a KÉSZÜLÉK boltját (és nem
  // mindig a Belvárost). A hook a korai `return`-ök ELŐTT fut – a React
  // hook-szabálya szerint minden renderben ugyanannyi hookot kell hívni.
  useActiveStoreEffect(device?.store);
  const [reservedUntil, setReservedUntil] = useState(null);  // ISO – mikor szabadul fel

  // ?foglalas=1 (a terméklista-modál CTA-járól): az űrlap azonnal nyílik,
  // amint a készülék betöltött és tudjuk, hogy NEM foglalt. Egyszer fut;
  // a szándék-esemény (trackReservationOpen) itt tüzel, mert a gombkattintás
  // – ami normál úton tüzelné – kimarad.
  useEffect(() => {
    if (autoReserveDone.current) return;
    if (searchParams.get('foglalas') !== '1') return;
    if (!device || notFound || reserved !== false) return;
    autoReserveDone.current = true;
    trackReservationOpen({
      device_id: device.uid,
      model: device.name,
      price: device.rawPrice,
      store: device.store,
      condition: device.condition,
      battery_health: device.battery,
    });
    setShowReservation(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, device, reserved, notFound]);
  const [expiryHours, setExpiryHours] = useState(24);   // a backend konfigból felülíródik
  // ?foglalas=1: a terméklista-modál „Lefoglalom 24 órára” gombjáról érkező
  // látogatónak azonnal nyitjuk az űrlapot – ne kelljen még egyszer kattintania.
  // Csak egyszer, csak betöltött és NEM foglalt készüléknél.
  const autoReserveDone = useRef(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let alive = true;  // uid-váltás/unmount után nincs setState (cache-elt kérés, nem abortálunk)
    const fetchDevice = async () => {
      try {
        // Munkamenet-cache (2 perc TTL): a lista-oldalról átjőve NEM tölti le
        // újra a teljes katalógust – ugyanabból a cache-elt válaszból keres.
        const json = await fetchJsonCached(STOREFRONT_API, { ttl: 120000 });
        if (!alive) return;
        const found = (json.data || []).find(d => d.unique_id === uid);
        if (found) {
          // A kategória EDDIG nem is létezett ezen az oldalon: a badge fixen
          // iPhone-kategóriát kapott, ezért AirPods/AirTag adatlapon is kiment
          // az akku%. Innentől a névből származó VALÓDI kategória megy, és az
          // akku is arra szűrve (constants/grades.js).
          const category = getCategory(found.model);
          setDevice({
            name:      normalizeName(found.model),
            price:     formatPrice(found.sell_price),
            rawPrice:  found.sell_price,
            condition: found.condition,
            category,
            spec:      found.specification,
            battery:   officeBattery(found, category),   // ugyanaz a kapu, mint a listán
            uid:       found.unique_id,
            store:     resolveStoreKey(OFFICE_STORE_FIELD_MAP[found.f_5rklnfbx49r]),
            notes:     found.notes,
            warranty:  found.warranty,
            image:     resolveImageUrl(found),
          });
        } else {
          setNotFound(true);
        }
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchDevice();
    return () => { alive = false; };
  }, [uid]);

  // Foglalt-e MOST ez a darab (a gomb állapotához). Csendben elbukik: ha nem
  // tudjuk lekérdezni, a gomb aktív marad – a szerver úgyis elutasítja, ha kell.
  useEffect(() => {
    if (!device?.uid) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reservations/status/${encodeURIComponent(device.uid)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setReserved(Boolean(d.reserved));
        setReservedUntil(d.expires_at || null);
        if (d.expiry_hours) setExpiryHours(d.expiry_hours);
      } catch { /* néma */ }
    })();
    return () => { alive = false; };
  }, [device?.uid]);

  // product_view (+ view_item) – termékoldal-MEGTEKINTÉSENKÉNT pontosan
  // egyszer: az újrarenderelés és a StrictMode nem duplikál, a „termék →
  // lista → ugyanaz a termék" SPA-út viszont új megtekintés. A payload
  // változatlan; a dedup-kulcs a hookban van – lásd utils/useProductView.js.
  useProductView(device);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ── CTA-megjelenés mérése ───────────────────────────────────────────────
  // A terméklap a /telefonok tölcsér utolsó lépése a lead előtt, ezért itt
  // kezdjük. A három fő CTA megjelenése adja a kattintási arány NEVEZŐJÉT –
  // e nélkül egy alacsony foglalásszám kétértelmű (lásd docs/cro-audit-cta.md).
  //
  // A hívások SZÁNDÉKOSAN a korai returnök ELŐTT állnak: a React hook-szabálya
  // szerint minden renderben ugyanannyi hooknak kell lefutnia, ugyanabban a
  // sorrendben. (A lint-kapu ezt el is kapta, amikor lejjebb voltak.)
  // A `device` ilyenkor még lehet null – a kontextust ezért védetten építjük;
  // a hook a ctx-et ref-ben tartja, tehát a mérés pillanatában már a betöltött
  // készülék adataival tüzel.
  //
  // PRIVACY-MINIMÁL: a `device_id` (a storefront unique_id-je = az IMEI utolsó
  // 4 jegye) SZÁNDÉKOSAN nincs benne – a CTA-teljesítményhez nem kell.
  // A `store` UGYANAZ a normalizált kulcs, amit a kattintás-események kapnak
  // (getStoreContact → 'belvaros' / 'tesco' / 'unknown'), így a megjelenés és
  // a kattintás boltonként összevethető.
  // A `uid` csak belső reset-kulcs: ha ugyanazon a komponens-példányon belül
  // termék→termék váltás történne, a mérés újraindul. Az eseménybe NEM kerül.
  const ctaCtx = device ? {
    store: getStoreContact(device.store).store,
    model: device.name,
    price: device.rawPrice,
    condition: device.condition,
    battery_health: device.battery,
  } : null;
  const foglalasRef = useCtaView(TERMEK_CTA.FOGLALAS, ctaCtx, uid);
  const hivasRef = useCtaView(TERMEK_CTA.HIVAS, ctaCtx, uid);
  const messengerRef = useCtaView(TERMEK_CTA.MESSENGER, ctaCtx, uid);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid var(--brand)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // K16: soft 404. A nem létező uid-ra (`/telefonok/999999`) az nginx a
  // SPA-shellt adja 200-zal – az nginx nem tudhatja, melyik uid létezik.
  // Eddig itt `<Navigate to="/telefonok/">` állt: a keresők a listaoldal
  // tartalmát látták a nem létező URL alatt (duplikált tartalom), és semmi
  // nem jelezte, hogy ez az oldal nem indexelendő.
  // Most: `noindex` + valódi magyarázat. Ez a felhasználónak is jobb – eddig
  // némán a listán találta magát, és nem tudta, miért.
  if (notFound) {
    return (
      <div className="min-h-screen bg-white" data-testid="phone-detail-notfound">
        <SEOHead
          title="Ez a készülék már nem elérhető"
          description="A keresett készülék már nem szerepel a készletünkben. Nézd meg az aktuális kínálatot."
          url={`https://norbiphone.hu/telefonok/${uid}`}
          noindex
          metaOnly
        />
        <div className="max-w-lg mx-auto px-4 pt-28 pb-20 text-center">
          <h1 className="text-2xl font-bold text-ink mb-3">Ez a készülék már nem elérhető</h1>
          <p className="text-gray-600 leading-relaxed mb-6">
            Elkelt, vagy kikerült a készletből. A jelenlegi kínálatunkban biztosan
            találsz hasonlót – és ha nem, szólj, és keresünk neked.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={h('/telefonok/')}
              className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors"
              data-testid="notfound-to-listing"
            >
              Aktuális készlet megtekintése
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE}`}
              onClick={() => trackCallClick('termek_nem_elerheto')}
              className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl border-2 border-gray-200 font-semibold text-ink hover:bg-gray-50 transition-colors"
            >
              Hívás – keresünk hasonlót
            </a>
          </div>
        </div>
      </div>
    );
  }

  const condLabel = normalizeGrade(device.condition)?.label || device.condition;
  const storeLabel = device.store ? STORE_LABELS[device.store] : 'Megtekinthető üzletünkben';
  // FELADAT 1: a KÉSZÜLÉK boltja dönti el a telefonszámot, a GBP-linket és az
  // útvonalat. Ismeretlen bolt → fő szám (getStoreContact fallback), de az
  // analytics `store` paramétere ilyenkor 'unknown', tehát mérhető marad.
  const contact = getStoreContact(device.store);
  // Egységes esemény-kontextus – minden CTA ugyanazt küldi (FELADAT 4).
  const evtCtx = {
    store: contact.store,
    device_id: device.uid,
    model: device.name,
    price: device.rawPrice,
    condition: device.condition,
    battery_health: device.battery,
  };
  // „vagyis” – adat → gyakorlati jelentés (sablonosan, a strukturált mezőkből)
  const batteryMeaning = batteryBenefit(device.battery);
  const storageMeaning = storageBenefit(device.spec);
  const gradeMeaning = gradeBenefit(normalizeGrade(device.condition)?.key);
  const gradeKeyFo = normalizeGrade(device.condition)?.key ?? null;
  const vanKep = Boolean(device.image) && hibasKep !== device.image;
  const kepesDevice = vanKep ? device : { ...device, image: null };
  const konkretElemek = konkretKeszulekElemek(kepesDevice, gradeKeyFo);

  const metaTitle = `${device.name} – ${condLabel} · ${device.price} | NorbiPhone`;
  const metaDesc  = `${device.name} eladó Orosházán. Állapot: ${condLabel}.${device.battery ? ` Akkumulátor: ${device.battery}.` : ''} ${device.spec || ''} Garanciával, NorbiPhone üzletből.`.trim();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Kezdőlap',  item: 'https://norbiphone.hu' },
      { '@type': 'ListItem', position: 2, name: 'Telefonok', item: 'https://norbiphone.hu/telefonok/' },
      { '@type': 'ListItem', position: 3, name: device.name, item: `https://norbiphone.hu/telefonok/${uid}/` },
    ],
  };

  // ── Product + Offer schema ─────────────────────────────────────────────────
  // MINDEN mező a készlet-API-ból (/api/storefront/devices) vagy a foglalás-
  // állapotból jön; kitalált érték nincs. Amihez nincs adat, az a node-ból
  // KIMARAD (hamis 0 Ft-ot vagy találgatott gyártót nem küldünk ki).
  const productUrl = `${BUSINESS.url}/telefonok/${uid}/`;   // perjellel = a canonical alak (audit #6)
  // Gyártó: az office API nem ad brand mezőt → a modellnévből (közös helper).
  const deviceBrand = getBrand(device.name);
  // A készülék egyedi azonosítója (az IMEI utolsó 4 jegye) – ez a bolti
  // cikkszám. A TELJES IMEI-t az API nem adja, és nem is publikálnánk.
  const sku = device.uid || undefined;
  // Állapot: a kanonikus grade-ből (constants/grades.js). Bontatlan (gyári
  // csomagolású, új) → NewCondition, minden más → UsedCondition – ugyanaz a
  // döntés, mint a /telefonok ItemList-schemájában. (A korábbi 'HIBAS' ág
  // halott volt: ilyen grade nincs.)
  const gradeKey = normalizeGrade(device.condition)?.key;
  const itemCondition = gradeKey === 'BONTATLAN'
    ? 'https://schema.org/NewCondition'
    : 'https://schema.org/UsedCondition';
  // Elérhetőség: a készlet-API csak eladható darabot ad vissza, de a 24 órás
  // foglalás alatt a darab nem vihető el → ilyenkor LimitedAvailability.
  // Amíg a foglaltság nem ismert (`reserved === null`), az InStock a helyes
  // alapérték (a lista-API már szűrt az eladhatóságra).
  const availability = reserved
    ? 'https://schema.org/LimitedAvailability'
    : 'https://schema.org/InStock';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: device.name,
    description: metaDesc,
    url: productUrl,
    ...(sku ? { sku } : {}),
    ...(deviceBrand ? { brand: { '@type': 'Brand', name: deviceBrand } } : {}),
    ...(device.image ? { image: device.image } : {}),
    itemCondition,
    // Az ár string is lehet a forrásban → csak VALÓDI számot küldünk ki.
    ...(typeof device.rawPrice === 'number' && device.rawPrice > 0 ? {
      offers: {
        '@type': 'Offer',
        url: productUrl,
        price: device.rawPrice,
        priceCurrency: 'HUF',
        availability,
        itemCondition,
        // Az eladó a @graph-ban kanonikusan definiált vállalkozás (SEOHead).
        seller: { '@id': BUSINESS.id },
      },
    } : {}),
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <SEOHead
          title={metaTitle}
          description={metaDesc}
          url={`https://norbiphone.hu/telefonok/${uid}`}
          image={device.image || undefined}
          ogType="product"
          schema={[
            breadcrumbSchema,
            productSchema,
          ]}
        />
        <section className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {/* Breadcrumb nav */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-6">
              {!bolt && (<><Link to="/" className="hover:text-brand">Kezdőlap</Link>
              <span>›</span></>)}
              <Link to={h('/telefonok/')} className="hover:text-brand">Telefonok</Link>
              <span>›</span>
              <span className="text-gray-500 truncate max-w-[200px]">{device.name}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Kép */}
              <div>
                {vanKep ? (
                  <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-50 border border-gray-100">
                    <ConditionBadge grade={device.condition} battery={device.battery} category={device.category} className="absolute top-3 left-3 z-10" />
                    <img
                      src={device.image}
                      alt={`${device.name}${device.spec ? `, ${device.spec}` : ''}, ${condLabel} állapot – a konkrét eladó készülék fotója`}
                      className={`w-full h-full object-contain ${reserved ? 'grayscale opacity-40' : ''}`}
                      // A hajtás FÖLÖTTI fő kép (LCP-elem): nem lusta, magas prioritás.
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      onError={() => setHibasKep(device.image)}
                    />
                    {/* Ugyanaz a vizuális nyelv, mint a listakártyán: aki a
                        listából érkezik, ugyanazt a jelet látja viszont. */}
                    {reserved && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="bg-red-600 text-white text-base font-extrabold tracking-[0.25em] px-6 py-2.5 rounded-lg shadow-xl -rotate-12">
                          FOGLALT
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Nincs saját fotó – ezt KIÍRJUK, nem hagyunk üres blokkot.
                     Az őszinte jelzés jobb, mint egy általános katalóguskép:
                     a vevő tudja, hogy a darabot személyesen érdemes megnézni. */
                  <div className="relative aspect-square rounded-2xl bg-gray-100 flex flex-col items-center justify-center gap-2 px-6 text-center">
                    <ConditionBadge grade={device.condition} battery={device.battery} category={device.category} className="absolute top-3 left-3 z-10" />
                    <span className="text-7xl" aria-hidden="true">📱</span>
                    <p className="text-sm font-semibold text-gray-600">Nincs saját fotó erről a készülékről</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Üzletünkben személyesen megnézheted – vagy hívj minket, és küldünk róla képet.
                    </p>
                  </div>
                )}
                {/* Q1: „Pont ezt a telefont kapom?” – a három bizonyíték (IMEI-vég,
                    mért akku, saját fotó) EGY sorban, közvetlenül a kép alatt.
                    Csak meglévő adatból; ami hiányzik, kimarad
                    (utils/termekBizonyitek.js). */}
                <p className="mt-2 text-xs text-center text-gray-500 leading-relaxed" data-testid="detail-konkret-keszulek">
                  {konkretElemek
                    ? konkretElemek.map((e, i) => (
                        <React.Fragment key={e}>
                          {i > 0 && ' · '}
                          {i === 0 ? <strong className="font-semibold text-ink">{e}</strong> : <span className="whitespace-nowrap">{e}</span>}
                        </React.Fragment>
                      ))
                    : 'Bontatlan, gyári csomagolású új termék.'}
                </p>
              </div>

              {/* Részletek */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-ink mb-2 leading-tight">
                  {device.name}
                </h1>
                {device.spec && (
                  <p className={`text-gray-500 text-sm ${storageMeaning ? 'mb-0.5' : 'mb-4'}`}>{device.spec}</p>
                )}
                {storageMeaning && (
                  <p className="text-xs text-gray-500 mb-4 leading-relaxed">{storageMeaning}</p>
                )}
                <div className="mb-6">
                  <div className="text-3xl font-bold text-brand">{device.price}</div>
                  {/* Q2/Q3: „Miért ennyi az ár?” – az ár indoklása egy sorban */}
                  <p className="mt-1.5 text-xs text-gray-500 leading-relaxed" data-testid="detail-ar-mikrosor">
                    {t(arMikrosorElemek(kepesDevice, gradeKeyFo).join(' · '))}
                    <span className="block">{AR_MIKROSOR_2}</span>
                  </p>
                </div>

                {/* ── Bizalmi sáv: kihez tartozik ez a darab, és mikor éred el ──
                    A kiírt szám annak az üzletnek a száma, ahol a készülék
                    FIZIKAILAG van – így nem a másik boltot hívja, ahol nem
                    tudnak róla. Csak akkor jelenik meg, ha a bolt tényleg
                    feloldható: ismeretlen boltnál a fő szám mellé odaírni egy
                    üzletnevet találgatás lenne. */}
                {contact.resolved && (
                  <div
                    className="mb-6 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-gray-600 leading-relaxed"
                    data-testid="detail-store-trust"
                  >
                    <span className="font-semibold text-ink">Kérdésed van erről a darabról?</span>{' '}
                    Hívd a {contact.shortLabelAccusative}:{' '}
                    <a
                      href={contact.telHref}
                      onClick={() => trackCallClick('termek_bizalmi_sav', evtCtx)}
                      aria-label={contact.callAriaLabel}
                      data-testid="detail-store-trust-call"
                      className="font-bold text-brand-hover underline underline-offset-2 whitespace-nowrap tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
                    >
                      {contact.phoneDisplay}
                    </a>
                    {contact.weekdayHours && <> ({contact.weekdayHours})</>} — ha nem vesszük
                    fel, visszahívunk.
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  {/* Állapot – kijelző/karc jelentése, grade szerint (őszinte) */}
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-base leading-5">✨</span>
                    <span>
                      Állapot: <strong>{condLabel}</strong>
                      {gradeMeaning && <span className="block text-xs text-gray-500 mt-0.5">{gradeMeaning}</span>}
                    </span>
                  </div>
                  {/* Akku (prioritás #2) */}
                  {device.battery && (
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-base leading-5">🔋</span>
                      <span>
                        Akkumulátor: <strong>{device.battery}</strong> — műszerrel lemérve
                        {batteryMeaning && <span className="block text-xs text-gray-500 mt-0.5">{batteryMeaning}</span>}
                      </span>
                    </div>
                  )}
                  {/* Bevizsgálva – az egyik legerősebb bizalmi üzenet */}
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-base leading-5">🔍</span>
                    <span>
                      Bevizsgálva
                      <span className="block text-xs text-gray-500 mt-0.5">vagyis {ELLENORZES_DB} ponton ellenőriztük, mielőtt a kínálatunkba került: {ELLENORZES_FOLYO}.</span>
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin size={16} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>
                      {storeLabel} — <strong className="font-semibold text-gray-700">kipróbálhatod, mielőtt fizetsz</strong>
                      {/* Útvonal a KÉSZÜLÉK boltjához (nem a másikhoz) */}
                      {contact.resolved && (
                        <>
                          {' · '}
                          <a
                            href={contact.directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackRouteClick('telefon_reszletek', evtCtx)}
                            data-testid="detail-cta-route"
                            className="text-brand hover:underline font-medium"
                          >
                            Útvonal
                          </a>
                        </>
                      )}
                    </span>
                  </div>
                  {/* Q4: „Mi lesz, ha elromlik?” – a „Nyugalom” pillér */}
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <ShieldCheck size={16} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>
                      {t(device.warranty || GARANCIA_HASZNALT_ROVID)}
                      <span className="block text-xs text-gray-500 mt-0.5">vagyis ha rendeltetésszerű használat mellett meghibásodik, számíthatsz a segítségünkre.</span>
                    </span>
                  </div>
                  {device.uid && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="text-base" aria-hidden="true">#</span>
                      <span className="text-gray-500">IMEI vége: <strong className="font-mono text-[#333333] tracking-widest">{device.uid}</strong></span>
                      <InfoTip text="Minden készülék egyedi azonosítója. A képeken pontosan ezt a darabot látod — átvételkor együtt ellenőrizhetjük." label="Mi ez a kód?" />
                    </div>
                  )}
                </div>

                {/* Fizetés + megosztás – a döntési pont közelében: a vevő itt
                    látja, hogyan fizethet, és innen tudja elküldeni a linket. */}
                <div className="mb-6 pt-5 border-t border-gray-100 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <PaymentMethods
                    note="A fizetés az üzletben történik, átvételkor."
                  />
                  <ShareButton
                    title={`${device.name} – ${device.price}`}
                    text={`${device.name} (${condLabel}) a NorbiPhone készletében Orosházán.`}
                    ctx={evtCtx}
                    className="self-start sm:self-auto flex-shrink-0"
                  />
                </div>

                {device.notes && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-gray-700 mb-6">
                    {device.notes}
                  </div>
                )}

                <div className="space-y-3">
                  {/* Sprint 2A: 24 órás félretétel – az elsődleges „nyomot hagyok”
                      lépés, ha a látogató nem tud/akar most telefonálni. */}
                  {reserved ? (
                    /* A foglaltság a LEGFONTOSABB információ ezen az oldalon:
                       enélkül a vásárló hiába indul el az üzletbe. Ezért nem
                       halvány szürke doboz, hanem hangsúlyos, piros keretes
                       blokk – vizuálisan erősebb, mint a mellette lévő CTA. */
                    <div
                      className="w-full rounded-xl border-2 border-red-300 bg-red-50 p-4"
                      role="status"
                      data-testid="detail-reserved-badge"
                    >
                      <p className="flex items-center gap-2 text-red-700 font-extrabold text-lg leading-tight">
                        <Clock size={20} className="flex-shrink-0" aria-hidden="true" />
                        Ez a készülék jelenleg FOGLALT
                      </p>
                      <p className="text-sm text-red-900/80 mt-1.5">
                        {remaining(reservedUntil)
                          ? <>Valaki félretetette – a foglalás még kb. <strong>{remaining(reservedUntil)}</strong> él. </>
                          : <>Valaki félretetette. </>}
                        Ha közben felszabadul, újra foglalható lesz. Hívj minket, és szólunk,
                        amint gazdátlan – vagy mutatunk hasonlót a készletből.
                      </p>
                    </div>
                  ) : (
                    /* Első személyű CTA: a látogató fejben befejezi a mondatot,
                       ez visz a kattintás felé. SZÁNDÉKOSAN nincs benne
                       „telefont”: ugyanez az adatlap szolgálja ki a MacBookot,
                       iPadet, AirPodsot, Apple Watchot és a konzolt is. Az órák
                       száma a backend konfigjából jön, nincs hardcode.
                       A gomb alatti sor a habozást veszi el (ingyenes, nem
                       kötelez) – ez hiányzott eddig. */
                    <>
                      {/* Nyugodt foglalás-üzenet – nem sürgetés, hanem
                          megnyugtatás: a készülék megvárja. */}
                      <div data-testid="detail-reserve-pitch">
                        <p className="font-bold text-ink leading-snug">Megtetszett?</p>
                        <p className="text-gray-600 text-sm mt-0.5">
                          Akkor ne hagyd, hogy más vigye el előled. Így nyugodtan
                          be tudsz jönni, megnézni, kipróbálni, és csak utána
                          dönteni.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          // Szándék-esemény a gombra: az open − submit különbség
                          // adja az űrlap-elhagyási arányt.
                          trackReservationOpen({
                            device_id: device.uid,
                            model: device.name,
                            price: device.rawPrice,
                            store: device.store,
                            condition: device.condition,
                            battery_health: device.battery,
                          });
                          setShowReservation(true);
                        }}
                        ref={foglalasRef}
                        data-testid="detail-cta-reserve"
                        className="flex items-center justify-center gap-2 w-full py-4 bg-brand text-white font-bold rounded-xl hover:bg-brand-hover transition-colors text-lg"
                      >
                        <Clock size={20} aria-hidden="true" />
                        Lefoglalom {expiryHours} órára
                      </button>
                      <p className="text-center text-xs text-gray-500 -mt-1">
                        Ingyenes, előleg és kötelezettség nélkül.
                      </p>
                    </>
                  )}
                  <a
                    href={contact.telHref}
                    ref={hivasRef}
                    onClick={() => trackCallClick('telefon_reszletek', evtCtx)}
                    data-testid="detail-cta-call"
                    className="flex items-center justify-center gap-2 w-full py-3.5 bg-white text-ink font-bold rounded-xl border-2 border-ink hover:bg-gray-50 transition-colors"
                  >
                    <Phone size={20} />
                    Érdeklődöm – Hívás
                  </a>
                  {/* A készülék boltja szerinti szám – így nem a másik üzletet hívja. */}
                  {contact.resolved && (
                    <p className="text-center text-xs text-gray-500 -mt-1">
                      {contact.name} · {contact.phoneDisplay}
                    </p>
                  )}
                  <a
                    href={contact.messengerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={messengerRef}
                    onClick={() => trackMessengerClick('telefon_reszletek', evtCtx)}
                    data-testid="detail-cta-messenger"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-messenger text-white font-semibold rounded-xl hover:bg-messenger-hover transition-colors"
                  >
                    <MessageCircle size={18} />
                    Messenger
                  </a>
                </div>
              </div>
            </div>

            {/* Trust badges grid */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { emoji: '📍', title: 'Kipróbálod, mielőtt fizetsz', desc: 'Orosházán, két üzletünkben kézbe veheted' },
                { emoji: '🔍', title: 'Bevizsgált készülék', desc: `${ELLENORZES_DB} ponton ellenőrizve` },
                { emoji: '🛡️', title: '3 hónap garancia', desc: bolt ? 'Garanciális hiba esetén segítünk' : 'Garanciális hiba esetén szervizünk segít' },
                { emoji: '🔄', title: 'Beszámítás', desc: 'A régi telefonod is beszámítjuk' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <span className="text-3xl mb-2">{item.emoji}</span>
                  <p className="font-semibold text-xs text-ink mb-1">{item.title}</p>
                  <p className="text-xs text-gray-500 leading-snug">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Garancia blokk – ugyanaz a három lépés, mint a /telefonok
                oldalon. A vevő félelme nem a garancia LÉTE, hanem hogy mi lesz
                vele ügyintézés közben; ezt itt, a döntés pillanatában kell
                megválaszolni. */}
            <div className="mt-6 p-5 sm:p-6 bg-brand/5 border border-brand/20 rounded-2xl">
              <h2 className="text-base sm:text-lg font-bold text-ink leading-snug mb-4">
                {t('Minden bevizsgált telefonra 3 hónap NorbiPhone szervizgarancia jár.')}
              </h2>
              <ol className="space-y-2.5 mb-5" data-testid="detail-warranty-steps">
                {[
                  'Behozod az üzletünkbe — nincs e-mail, nincs várólista',
                  bolt ? 'A garanciális ügyintézés idejére cseretelefont biztosítunk' : 'A javítás idejére cseretelefont biztosítunk',
                  'Ha nem garanciás, azt is megmutatjuk, miért',
                ].map((lepes, i) => (
                  <li key={lepes} className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed">{lepes}</span>
                  </li>
                ))}
              </ol>
              <h2 className="text-base font-bold text-ink mb-4 flex items-center gap-2 border-t border-brand/20 pt-4">
                <ShieldCheck size={18} className="text-brand" />
                Miért vásárolj tőlünk?
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <p className="font-semibold text-sm text-ink mb-1">🛡️ Garancia, nem csak ígéret</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{bolt
                    ? 'Minden használt készülékre 3 hónap garancia. Garanciális hiba esetén fordulj hozzánk bizalommal — itt vagyunk Orosházán, két üzletben.'
                    : 'Használt vásárlásra 3 hónap, nálunk végzett javításra 6 hónap szervizgarancia. Garanciális hiba esetén fordulj hozzánk bizalommal — saját szervizünk itt van Orosházán.'}</p>
                </div>
                <div>
                  <p className="font-semibold text-sm text-ink mb-1">📍 Kipróbálod, mielőtt fizetsz</p>
                  <p className="text-xs text-gray-600 leading-relaxed">Kézbe veszed és kipróbálod az üzletben, mielőtt fizetsz. Amit az oldalon látsz, azt a helyszínen ellenőrizheted.</p>
                </div>
                <div>
                  <p className="font-semibold text-sm text-ink mb-1">🔍 Bevizsgált, mért, számlás</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{ELLENORZES_DB} ponton ellenőrizve, az akkutól a vízkárnyomokig. Számlát adunk, az IMEI ellenőrzött, az eredet tiszta.</p>
                </div>
              </div>
            </div>

            {/* Belső linkek */}
            <div className="mt-8 pt-8 border-t border-gray-100 flex flex-wrap items-center gap-4">
              <Link
                to={h('/telefonok/')}
                className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
              >
                <ArrowLeft size={18} />
                Összes telefon
              </Link>
              <span className="text-gray-200">|</span>
              <Link
                to={h('/felvasarlas/')}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-brand transition-colors text-sm"
              >
                <RefreshCw size={15} />
                A régidet felvásároljuk
              </Link>
              {!bolt && (<>
              <span className="text-gray-200">|</span>
              <Link
                to="/szerviz/"
                className="text-gray-600 hover:text-brand transition-colors text-sm"
              >
                Szerviz és garancia
              </Link>
              </>)}
            </div>
          </div>
        </section>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 sm:bottom-6 left-4 sm:left-6 z-40 w-11 h-11 sm:w-12 sm:h-12 bg-brand text-white rounded-full shadow-lg hover:bg-brand-hover transition-all hover:scale-110 flex items-center justify-center"
          aria-label="Ugrás a tetejére"
        >
          <ChevronUp size={24} />
        </button>
      )}

      {/* Sprint 2A – 24 órás foglalás. Sikeres beküldés után a gomb átvált
          „lefoglalva” állapotba (a szerver úgyis blokkolná a másodikat). */}
      {showReservation && (
        <ReservationModal
          device={device}
          onClose={() => setShowReservation(false)}
          onSuccess={() => setReserved(true)}
          expiryHours={expiryHours}
        />
      )}
    </>
  );
}
